import { FC, useCallback, useMemo } from 'react'
import cn from 'classnames'
import themes from 'styles/base/themes.styl'
import { useStoreDispatch, useStoreSelector } from 'helpers/reactReduxHelpers'
import { CreditCardPanePreset } from 'components/features/creditCard/creditCardPanes/CreditCardPanePreset'
import { cardPaymentMethodsSelector, creditCardErrorsSelector } from 'components/features/creditCard/creditCardStore/selectors'
import { useSimpleGuard } from 'helpers/loadableHelpers'
import { ccPaymentMethodsGuard } from 'components/features/creditCard/creditCardStore/guards/ccPaymentMethodsGuard'
import LoadingOverlay from 'components/atoms/preloaders/LoadingOverlay'
import { ResetCreateCCPaymentMethodAction, ResetDeleteCCPaymentMethodAction } from 'components/features/creditCard/creditCardStore/actions'
import FloatingPaneNotification from 'components/features/pfm/FloatingPaneNotification/FloatingPaneNotification'
import { AddPaymentMethodsPaneButton, BACK_BUTTON_PROPS, CANCEL_BUTTON_PROPS, KillAllPanesButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import { PaymentMethodsPaneButton } from 'components/features/pfm/Button/FloatingPaneButtons'
import CCPaymentMethodItem from 'components/features/creditCard/creditCardPanes/paneComponents/CardPaymentMethodItem'
import Flex from 'components/atoms/Flex/Flex'
import Separator from 'components/atoms/Separator/Separator'
import React from 'react'

const PaymentMethodsPane: FC<{}> = () => {
  const paymentMethods = useStoreSelector(cardPaymentMethodsSelector)
  const errors = useStoreSelector(creditCardErrorsSelector)

  const isLoaded = useSimpleGuard(ccPaymentMethodsGuard, {}, 'CreditCardPaymentMethodsPane')

  const dispatch = useStoreDispatch()
  const handleResetCCRequests = useCallback(() => {
    dispatch(new ResetCreateCCPaymentMethodAction())
    dispatch(new ResetDeleteCCPaymentMethodAction())
  }, [dispatch])

  const errorChild = useMemo(() => errors?.length > 0 && (
    <FloatingPaneNotification
      messages={ errors }
      onDismiss={ handleResetCCRequests }
    />
  ), [errors, handleResetCCRequests])

  const zeroStateChild = useMemo(() => (
    <li key='zeroState' className={ cn(themes.textOnly) } style={{ textAlign: 'center' }}>
      <br />
      <h4>You have no saved payment methods</h4>
      <br />
      <p>Add a bank account to begin making card payments</p>
      <br />
    </li>
  ), [])

  const paymentMethodsChild = useMemo(() => paymentMethods?.map((item, idx) => (
    <React.Fragment key={ idx }>
      <CCPaymentMethodItem method={ item } />
      { idx !== paymentMethods?.length - 1 && <Separator key={ idx.toString() + 'separator' } asListItem /> }
    </React.Fragment>
  )), [paymentMethods])

  const contentChild = useMemo(() => {
    if (!isLoaded) {
      return <LoadingOverlay />
    }

    return (
      <>
        <p>These are the accounts you can use to pay off your card balance.</p>
        <Flex component='ul' gap='medium' padding='defaultSize' theme={ !!paymentMethods ? 'info' : 'lightOrange' }>
          { paymentMethodsChild || zeroStateChild }
        </Flex>
        <AddPaymentMethodsPaneButton
          text='Add A Bank Account'
          backButton={ <PaymentMethodsPaneButton { ...BACK_BUTTON_PROPS } /> }
          fullWidth
        />
      </>
    )
  }, [isLoaded, paymentMethods, paymentMethodsChild, zeroStateChild])

  return (
    <CreditCardPanePreset
      title='Payment Methods'
      notification={ errorChild }
      tertiaryButton={ <KillAllPanesButton { ...CANCEL_BUTTON_PROPS } text='Close' /> }
    >
      { contentChild }
    </CreditCardPanePreset>
  )
}

export default PaymentMethodsPane
